import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TemplateGeneratorService} from '@app/services/template-generator.service';
import moment from 'moment-timezone';
import * as routes from '@app/routes';

import {LANDING_PAGE_TEMPLATE_TYPE_ID, INFLUENCER_TYPES} from '@app/consts';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {select, Store} from '@ngrx/store';
import {
  getInfluencersTypes,
  getTemplateCampaign,
  IInfluencersState,
  InfluencersTypeList,
  ITemplateState, ResetTemplateState,
  TemplateCampaign
} from '@app/stores';
import {takeUntil} from 'rxjs/operators';
import {lastValueFrom, Subject, Subscription} from 'rxjs';
import {InfluencerType} from '@app/modules/shared/models';
import * as _ from 'lodash';
import {SocketService} from '@app/services/socket.service';
import { RenameTitleModalComponent } from '@app/modules/rename-title-modal/rename-title-modal.component';
import { TemplateService } from '@app/services/template.service';
import Swal from 'sweetalert2';
import { PageShellComponent } from '../../../_components/page-shell/page-shell.component';
import { StatusBarComponent } from '../../status-bar/status-bar.component';
import { MatIcon } from '@angular/material/icon';
import { LazyBackgroundDirective } from './lazy-background.directive';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';

@Component({
  selector: 'app-select-template',
  templateUrl: './select-template.component.html',
  styleUrls: ['./select-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PageShellComponent, StatusBarComponent, MatIcon, LazyBackgroundDirective, MatMenuTrigger, MatMenu]
})
export class SelectTemplateComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private templateStore = inject<Store<ITemplateState>>(Store);
  private influencerStore = inject<Store<IInfluencersState>>(Store);
  private templateGeneratorService = inject(TemplateGeneratorService);
  private modalService = inject(NgbModal);
  private readonly sbModal = inject(SbModalService);
  private socketService = inject(SocketService);
  private readonly templateService = inject(TemplateService);

  readonly step = signal<any>(undefined);
  readonly campaign = signal<any>(undefined);
  readonly segment = signal<any>(undefined);
  readonly snoTemplates = signal<any[]>([]);
  readonly clientTemplates = signal<any[]>([]);
  public campaignTemplate: any = {};
  public currentClientTemplate;
  readonly thumbnail = signal<string>(undefined);
  public thumbnailModel: any;
  unsubscriber = new Subject();
  subscription: Subscription;
  private readonly influencerTypes = signal<InfluencerType[]>([]);

  constructor() {
    this.templateStore.dispatch(ResetTemplateState({params: {templateCampaign: null}}));
    this.subscribeStore();
    this.subscription = this.socketService.sourceData
      .subscribe((message: any) => {
        if (message?.data?.requesterId) {
          let temp = _.cloneDeep(this.clientTemplates());
          temp.map(element => {
            if (element.id === message.data.requesterId && message.data?.requesterType === 'template') {
              element.thumbnail = message.data.result.thumbnail;
            }
            return element;
          });
          this.clientTemplates.set([...temp]);

          temp = _.cloneDeep(this.snoTemplates());
          temp.map(element => {
            if (element.id === message.data.requesterId && message.data?.requesterType === 'template') {
              element.thumbnail = message.data.result.thumbnail;
            }
            return element;
          });
          this.snoTemplates.set([...temp]);
        }
      },
      (err) => console.error(err),
      () => console.warn('Completed!')
      );
  }

  subscribeStore() {
    this.templateStore.pipe(select(getTemplateCampaign))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(templateCampaign => {
        if (templateCampaign) {
          this.clientTemplates.set(templateCampaign.templates);
          this.snoTemplates.set(templateCampaign.publicTemplates);
          this.campaignTemplate = templateCampaign.campaignTemplate;
          if (this.campaignTemplate && !this.activatedRoute.snapshot.queryParams?.select) {
            this.onNextButtonClick(this.campaignTemplate);
            return;
          }
          // A campaign without a landing page starts in the new editor. This gallery lists the old
          // editor's templates; Change template on the landing page still opens it (?select).
          if (!this.campaignTemplate && !this.activatedRoute.snapshot.queryParams?.select) {
            this.blankTemplateRedirect();
            return;
          }
          // if (this.campaignTemplate) {
          //   this.clientTemplates = [
          //     this.campaignTemplate,
          //     ...this.clientTemplates
          //   ];
          // }
        }
      });
    this.influencerStore.pipe(select(getInfluencersTypes))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(influencerTypes => {
        if (influencerTypes && influencerTypes.length > 0) {
          this.influencerTypes.set(influencerTypes);
        }
      });
  }

  ngOnInit() {
    this.templateGeneratorService.forceReloadCampaign();
    this.templateGeneratorService.subscribeStore();
    this.templateGeneratorService.clearTemplate(LANDING_PAGE_TEMPLATE_TYPE_ID);
    this.templateGeneratorService.onCampaignLoaded
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(campaign => {
        if (!campaign) {
          return;
        }

        this.campaign.set(campaign);

        if (!campaign.segments.length) {
          this.router.navigateByUrl(routes.addInfluencers({projectId: campaign.project_id}, {campId: campaign.id}));
          return;
        }

        this.segment.set(campaign.segments[0]);
        this.setStatusBar();

        const params = {
          campaign_id: campaign.id,
          influencer_type_id: this.segment().influencer_type_id,
          template_type_id: LANDING_PAGE_TEMPLATE_TYPE_ID
        };
        this.templateStore.dispatch(TemplateCampaign({params}));
        this.influencerStore.dispatch(InfluencersTypeList());
      });
  }

  setStatusBar() {
    const campaign = this.campaign();
    this.step.set({
      title: 'Content',
      params: {
        projectId: campaign.project_id,
        campaignId: campaign.id,
        campaignName: campaign.name
      }
    });
  }

  onTemplateSelect(type, index, force) {
    const template = this[type]()[index];
    if (template.campaign_id !== this.campaign().id) {
      force = true;
    }
    if (force) {
      this.templateGeneratorService.touchTemplate({templateTypeId: LANDING_PAGE_TEMPLATE_TYPE_ID, sourceTemplateId: template.id});
    }

    this.onNextButtonClick(template);
  }

  onSaveButtonClick() {
    this.router.navigateByUrl('/' + routes.campaigns({projectId: this.campaign().project_id}));
  }

  parseTimeAgo(timeStamp) {
    return moment(timeStamp).fromNow();
  }

  influencerType() {
    const segment = this.segment();
    if (!segment) {
      return;
    }
    const influencerType = this.influencerTypes().find(item => item.id === Number(segment.influencer_type_id));
    if (influencerType) {
      return influencerType.title.toLowerCase();
    }
    return '';
  }

  onBackButtonClick() {
    this.router.navigateByUrl(routes.addInfluencers({projectId: this.campaign().project_id}, {campId: this.campaign().id}));
  }

  onNextButtonClick(template: any = null) {
    let params: any = {blank: true};
    if (template) {
      params = {sourceTemplateId: template.id};
    } else {
      if (this.campaignTemplate) {
        params = {sourceTemplateId: this.campaignTemplate.id};
      }
    }
    this.router.navigateByUrl(routes.campaignTemplates({projectId: this.campaign().project_id, campaignId: this.campaign().id}, params));
  }

  blankTemplateRedirect() {
    const params: any = {blank: true};
    this.router.navigateByUrl(routes.campaignTemplates({projectId: this.campaign().project_id, campaignId: this.campaign().id}, params));
  }

  showTemplateThumbnail(content, thumbnail: string) {
    if (thumbnail) {
      this.thumbnailModel = this.modalService.open(content, {size: 'xl', centered: true});
      this.thumbnail.set(thumbnail);
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
    this.templateGeneratorService.unsubscribe();
    this.subscription.unsubscribe();
  }

  openRenameModal(template: any) {
    //
    // Validate template
    if (!template?.id) {
      return;
    }

    //
    // Open modal
    const modelRef = this.sbModal.openComponent(RenameTitleModalComponent, {
      centered: true,
      size: 'sm',
      keyboard: true,
      windowClass: 'sb-entity-form-modal',
    }, {
      title: 'Template',
      value: template.name,
    });

    //
    // On close modal event
    // should validate and update the template
    modelRef.result.then(async (result: any = {}) => {
      //
      // Validate result
      if (!result?.value) {
        return;
      }
      // update database and reload
      await lastValueFrom(this.templateService.updateAll(template.id, { name: result.value }));
      this.updateTemplates();
    });
  }

  async removeTemplate(template: any) {
    const confirm: any = await Swal.fire({
      title: 'Delete Template?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    });

    //
    // Validate
    if(!confirm.value) {
      return;
    }

    //
    // Delete
    await lastValueFrom(this.templateService.deleteTemplate(template.id));
    // update templates
    this.updateTemplates();
  }

  updateTemplates() {
    this.templateStore.dispatch(
      TemplateCampaign({
        params: {
          campaign_id: this.campaign().id,
          influencer_type_id: this.segment().influencer_type_id,
          template_type_id: LANDING_PAGE_TEMPLATE_TYPE_ID,
        }
      })
    );
  }
}
